import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Eye, Clock, User, Pencil } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';

export default function AdminPending() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewArticle, setPreviewArticle] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPendingArticles();
  }, []);

  const fetchPendingArticles = async () => {
    try {
      const response = await adminAPI.getPendingArticles();
      setArticles(response.data);
    } catch (error) {
      toast.error('Failed to load pending articles');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (article) => {
    setProcessing(true);
    try {
      await adminAPI.approveArticle(article.id);
      toast.success('Article approved and published!');
      fetchPendingArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve article');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectClick = (article) => {
    setSelectedArticle(article);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    try {
      await adminAPI.rejectArticle(selectedArticle.id, rejectReason);
      toast.success('Article sent back for revision');
      setRejectDialogOpen(false);
      fetchPendingArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject article');
    } finally {
      setProcessing(false);
    }
  };

  const handlePreview = async (article) => {
    setPreviewLoading(true);
    setPreviewDialogOpen(true);
    try {
      const response = await adminAPI.previewArticle(article.id);
      setPreviewArticle(response.data);
    } catch (error) {
      toast.error('Failed to load article preview');
      setPreviewDialogOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="admin-pending">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">Pending Review</h1>
          <p className="text-muted-foreground">Review and approve articles submitted by authors.</p>
        </div>
        <div className="space-y-4">
          <div className="bg-background p-6 border border-border animate-pulse">
            <div className="h-6 bg-muted rounded w-1/3 mb-2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div data-testid="admin-pending">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">Pending Review</h1>
          <p className="text-muted-foreground">Review and approve articles submitted by authors.</p>
        </div>
        <div className="bg-background p-12 border border-border text-center">
          <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="font-heading text-xl font-semibold text-foreground mb-2">No Pending Articles</h3>
          <p className="text-muted-foreground">All submitted articles have been reviewed.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-pending">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground mb-2">Pending Review</h1>
        <p className="text-muted-foreground">Review and approve articles submitted by authors.</p>
      </div>

      <div className="space-y-4">
        {articles.map((article) => (
          <div key={article.id} className="bg-background p-6 border border-border hover:border-accent/50 transition-colors">
            <div className="flex flex-col lg:flex-row justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-xl font-semibold text-foreground">{article.title}</h3>
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-800">
                    <Clock className="h-3 w-3" />
                    Pending
                  </span>
                </div>
                <p className="text-muted-foreground mb-3 line-clamp-2">{article.excerpt}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    {article.author_name}
                  </span>
                  <span>Category: {article.category}</span>
                  <span>Submitted: {new Date(article.submitted_at).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 lg:flex-col lg:items-end">
                <Button variant="outline" size="sm" onClick={() => handlePreview(article)} data-testid={`preview-${article.id}`}>
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </Button>
                <Link to={`/admin/articles/${article.id}/edit`}>
                  <Button variant="outline" size="sm" data-testid={`edit-${article.id}`}>
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                </Link>
                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApprove(article)} disabled={processing} data-testid={`approve-${article.id}`}>
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleRejectClick(article)} disabled={processing} data-testid={`reject-${article.id}`}>
                  <X className="h-4 w-4 mr-1" />
                  Request Revision
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Article Preview</DialogTitle>
            <DialogDescription>Preview the article before approving or requesting revisions.</DialogDescription>
          </DialogHeader>
          {previewLoading && <div className="py-12 text-center"><p className="text-muted-foreground">Loading preview...</p></div>}
          {previewArticle && (
            <div className="py-4 space-y-4">
              <h2 className="font-heading text-2xl font-bold">{previewArticle.title}</h2>
              <p className="text-muted-foreground">By {previewArticle.author_name}</p>
              <div className="p-4 bg-muted/30 border-l-4 border-accent italic">{previewArticle.excerpt}</div>
              <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: previewArticle.content }} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>Close</Button>
            {previewArticle && (
              <Link to={`/admin/articles/${previewArticle.id}/edit`}>
                <Button variant="outline"><Pencil className="h-4 w-4 mr-1" />Edit</Button>
              </Link>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Revision</DialogTitle>
            <DialogDescription>Provide feedback to help the author improve their article.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-foreground mb-2 block">Reason for revision request *</label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Please specify what needs to be improved..." rows={4} data-testid="reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectConfirm} disabled={processing || !rejectReason.trim()}>Send Revision Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
